import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Home, Bed, Bath, Sofa, MapPin, ArrowUpRight, Search, X, Mail } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  'For Sale':   { label: 'For Sale',   className: 'bg-emerald-500 text-white' },
  'Under Offer':{ label: 'Under Offer',className: 'bg-amber-500 text-white'   },
  'Sold STC':   { label: 'Sold STC',   className: 'bg-rose-500 text-white'    },
  'Reserved':   { label: 'Reserved',   className: 'bg-slate-700 text-white'   },
};

const formatPrice = (p, qual, display) => {
  if (!p || display === false) return 'POA';
  const num = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(p);
  if (!qual || qual === 'Default') return num;
  const map = {
    'Offers Over': 'Offers Over ', 'Offers Around': 'Offers Around ',
    'Guide Price': 'Guide Price ', 'Fixed Price': 'Fixed Price ',
    'Offers In Excess Of': 'Offers In Excess Of ', 'From': 'From ',
  };
  return `${map[qual] || ''}${num}`;
};

const PropertyCard = ({ p, onOpen }) => {
  const hero = (p.images?.[0]?.urls?.hero) || (p.images?.[0]?.url) || '';
  const status = STATUS_STYLES[p.status] || { label: p.status, className: 'bg-slate-500 text-white' };
  return (
    <button
      onClick={() => onOpen(p.id)}
      className="group text-left rounded-2xl overflow-hidden bg-white ring-1 ring-slate-200/70 hover:ring-cyan-400 hover:shadow-2xl transition-all"
      data-testid={`pennylane-card-${p.id}`}
    >
      <div className="relative aspect-[4/3] bg-slate-200 overflow-hidden">
        {hero ? (
          <img src={hero} alt={p.inline_address} loading="lazy"
               className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
               onError={(e) => { e.target.style.opacity = '0.2'; }} />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-slate-400"><Home size={48} /></div>
        )}
        <span className={`absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${status.className}`}>
          {status.label}
        </span>
        {p.images?.length > 1 && (
          <span className="absolute bottom-3 right-3 text-[10px] font-semibold bg-black/60 backdrop-blur-sm text-white px-2 py-0.5 rounded-full">
            {p.images.length} photos
          </span>
        )}
      </div>
      <div className="p-4">
        <p className="text-xl font-bold text-slate-900 tracking-tight">
          {formatPrice(p.price, p.price_qualifier, p.display_price)}
        </p>
        <p className="text-sm font-semibold text-slate-800 mt-1 line-clamp-1" title={p.inline_address}>
          {p.inline_address}
        </p>
        <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
          {p.bedrooms != null && (<span className="inline-flex items-center gap-1"><Bed size={13}/> {p.bedrooms}</span>)}
          {p.bathrooms != null && (<span className="inline-flex items-center gap-1"><Bath size={13}/> {p.bathrooms}</span>)}
          {p.receptions != null && (<span className="inline-flex items-center gap-1"><Sofa size={13}/> {p.receptions}</span>)}
          {p.property_type && <span className="ml-auto text-slate-400">{p.property_type}</span>}
        </div>
      </div>
    </button>
  );
};

const EnquiryForm = ({ property }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', website: '' });
  const [state, setState] = useState('idle');
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState('sending'); setError('');
    try {
      const r = await fetch(`${API}/pennylane/properties/${property.id}/enquiry`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Something went wrong. Please try again.');
      }
      setState('sent');
    } catch (err) { setError(err.message); setState('idle'); }
  };

  if (state === 'sent') {
    return (
      <div className="mt-6 rounded-xl bg-emerald-50 ring-1 ring-emerald-200 p-5" data-testid="enquiry-success">
        <p className="text-sm font-semibold text-emerald-900">Your enquiry is in.</p>
        <p className="text-sm text-emerald-800 mt-1">
          We are passing it to {property.branch_name || 'the selling agent'}, who will contact you
          directly about viewings. Check your inbox for a copy.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-6 rounded-xl bg-slate-50 ring-1 ring-slate-200 p-5" data-testid="enquiry-form">
      <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-1">Ask about this home</h3>
      <p className="text-xs text-slate-500 mb-4">
        Sent straight to us and passed to {property.branch_name || 'the selling agent'}. No cost, no obligation.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <input required value={form.name} onChange={set('name')} placeholder="Your name"
               className="text-sm border border-slate-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-cyan-400"
               data-testid="enquiry-name" />
        <input required type="email" value={form.email} onChange={set('email')} placeholder="Email address"
               className="text-sm border border-slate-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-cyan-400"
               data-testid="enquiry-email" />
        <input value={form.phone} onChange={set('phone')} placeholder="Phone (optional)"
               className="text-sm border border-slate-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-cyan-400 sm:col-span-2"
               data-testid="enquiry-phone" />
        <textarea required rows={3} minLength={10} value={form.message} onChange={set('message')}
                  placeholder="When could you view it? Anything you want to ask?"
                  className="text-sm border border-slate-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-cyan-400 sm:col-span-2"
                  data-testid="enquiry-message" />
      </div>
      {/* Honeypot: hidden from people, irresistible to bots */}
      <input value={form.website} onChange={set('website')} tabIndex={-1} autoComplete="off"
             aria-hidden="true" className="hidden" />
      {error && <p className="text-xs text-rose-600 mt-3" data-testid="enquiry-error">{error}</p>}
      <button type="submit" disabled={state === 'sending'}
              className="mt-4 inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
              data-testid="enquiry-submit">
        <Mail size={14} /> {state === 'sending' ? 'Sending...' : 'Send enquiry'}
      </button>
    </form>
  );
};

const PropertyDetail = ({ id, onClose }) => {
  const [p, setP]         = useState(null);
  const [imgIdx, setIdx]  = useState(0);
  useEffect(() => {
    if (!id) return;
    (async () => {
      const r = await fetch(`${API}/pennylane/properties/${id}`);
      if (r.ok) setP(await r.json());
    })();
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [id]);

  useEffect(() => {
    if (!p) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setIdx((i) => (i + 1) % (p.images?.length || 1));
      if (e.key === 'ArrowLeft')  setIdx((i) => (i - 1 + (p.images?.length || 1)) % (p.images?.length || 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [p, onClose]);

  if (!id) return null;
  const img = p?.images?.[imgIdx];
  const status = p ? (STATUS_STYLES[p.status] || { label: p.status, className: 'bg-slate-500 text-white' }) : null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-testid="pennylane-detail-modal">
      <div className="min-h-screen p-4 md:p-8 flex items-start justify-center">
        <div className="bg-white rounded-2xl max-w-5xl w-full overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={onClose}
            className="absolute top-6 right-6 z-20 w-10 h-10 rounded-full bg-white/90 text-slate-900 hover:bg-white shadow-lg inline-flex items-center justify-center transition-colors"
            data-testid="pennylane-detail-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
          {p ? (
            <>
              {/* Hero image + gallery strip */}
              <div className="relative aspect-video bg-slate-200 overflow-hidden">
                {img?.urls?.hero || img?.url ? (
                  <img src={img.urls?.hero || img.url} alt={p.inline_address}
                       className="absolute inset-0 w-full h-full object-cover"/>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-slate-400"><Home size={64}/></div>
                )}
                {status && (
                  <span className={`absolute top-4 left-4 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded ${status.className}`}>
                    {status.label}
                  </span>
                )}
                {p.images?.length > 1 && (
                  <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2 overflow-x-auto scrollbar-hide">
                    {p.images.slice(0, 10).map((im, i) => (
                      <button key={im.id}
                              onClick={() => setIdx(i)}
                              className={`shrink-0 w-16 h-12 rounded overflow-hidden ring-2 transition-all ${i === imgIdx ? 'ring-white' : 'ring-white/40 hover:ring-white/70'}`}>
                        <img src={im.urls?.thumbnail || im.urls?.small || im.url} alt="" className="w-full h-full object-cover"/>
                      </button>
                    ))}
                    {p.images.length > 10 && (
                      <span className="shrink-0 text-white/80 text-xs font-medium px-2">+{p.images.length - 10} more</span>
                    )}
                  </div>
                )}
              </div>

              <div className="p-6 md:p-8">
                <p className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
                  {formatPrice(p.price, p.price_qualifier, p.display_price)}
                </p>
                <h2 className="text-lg font-semibold text-slate-700 mt-1 inline-flex items-start gap-2">
                  <MapPin size={16} className="mt-1 text-cyan-600 shrink-0"/> {p.inline_address}
                </h2>
                <div className="flex items-center flex-wrap gap-6 mt-5 text-sm">
                  {p.bedrooms != null && (<span className="inline-flex items-center gap-1.5 text-slate-700"><Bed size={16} className="text-cyan-600"/> <b>{p.bedrooms}</b> bed</span>)}
                  {p.bathrooms != null && (<span className="inline-flex items-center gap-1.5 text-slate-700"><Bath size={16} className="text-cyan-600"/> <b>{p.bathrooms}</b> bath</span>)}
                  {p.receptions != null && (<span className="inline-flex items-center gap-1.5 text-slate-700"><Sofa size={16} className="text-cyan-600"/> <b>{p.receptions}</b> reception</span>)}
                  {p.property_type && (<span className="text-slate-500">{p.property_type}</span>)}
                  {p.tenure && (<span className="text-slate-500">{p.tenure}</span>)}
                  {p.epc_rating && (<span className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded bg-emerald-100 text-emerald-800">EPC {p.epc_rating}</span>)}
                </div>

                {p.short_description && (
                  <p className="mt-6 text-slate-700 leading-relaxed">{p.short_description}</p>
                )}
                {p.full_description && (
                  <div className="mt-6 prose prose-sm max-w-none text-slate-700"
                       dangerouslySetInnerHTML={{ __html: p.full_description }}/>
                )}

                {p.features?.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3">Features</h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-slate-700">
                      {p.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="mt-1 w-1.5 h-1.5 rounded-full bg-cyan-500 shrink-0"/>{f}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-8 pt-6 border-t border-slate-200 flex items-center justify-between flex-wrap gap-3">
                  <p className="text-xs text-slate-500">
                    Marketed by <b className="text-slate-800">{p.branch_name || 'the selling agent'}</b>
                    {p.council_tax_band && <> · Council tax band <b>{p.council_tax_band}</b></>}
                  </p>
                  {p.public_url && (
                    <a href={p.public_url} target="_blank" rel="noopener noreferrer"
                       onClick={() => {
                         // keepalive so the count survives the page unloading
                         fetch(`${API}/pennylane/properties/${p.id}/click`,
                               { method: 'POST', keepalive: true }).catch(() => {});
                       }}
                       className="inline-flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                       data-testid="pennylane-detail-external">
                      View listing <ArrowUpRight size={14}/>
                    </a>
                  )}
                </div>

                <EnquiryForm property={p} />
              </div>
            </>
          ) : (
            <div className="p-16 text-center text-slate-400">Loading...</div>
          )}
        </div>
      </div>
    </div>
  );
};

const PennyLaneProperties = () => {
  const [data, setData]     = useState({ items: [], total: 0 });
  const [loading, setLoad]  = useState(true);
  const [openId, setOpenId] = useState(null);
  // Buyers should land on homes they can actually offer on, so the page
  // opens filtered to available listings rather than under-offer stock.
  const [filters, setF]     = useState({ status: 'For Sale', min_beds: '' });
  const counts = data.counts || { available: 0, under_offer: 0, agents: 0 };

  const load = useCallback(async () => {
    setLoad(true);
    const q = new URLSearchParams({ limit: '48' });
    if (filters.status)   q.set('status', filters.status);
    if (filters.min_beds) q.set('min_beds', filters.min_beds);
    try {
      const r = await fetch(`${API}/pennylane/properties?${q}`);
      const d = await r.json();
      setData(d);
    } catch { /* empty */ } finally { setLoad(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="pennylane-page">
      <SEO title="Properties for Sale in Paisley & Renfrewshire" description="Live properties for sale in Paisley, Renfrewshire and Greater Glasgow. Updated hourly, with the selling agent shown on every listing." url="/for-sale"/>
      <Header />
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 text-white py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_20%_20%,white_1px,transparent_1px)] [background-size:24px_24px]"/>
        <div className="max-w-6xl mx-auto px-6 relative">
          <p className="text-xs uppercase tracking-[0.3em] font-bold text-cyan-300 mb-3">Property</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight" data-testid="pennylane-h1">
            Properties for Sale
          </h1>
          <p className="mt-4 max-w-2xl text-slate-300 text-lg">
            {counts.available > 0
              ? `${counts.available} homes for sale from ${counts.agents} estate agents across Paisley, Renfrewshire and Greater Glasgow.`
              : 'Loading listings...'}
            {' '}Updated through the day, with the selling agent shown on every listing.
          </p>
        </div>
      </section>

      {/* Filter bar */}
      <section className="sticky top-24 z-30 bg-white/85 backdrop-blur-md border-b border-slate-200/70">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-3 flex-wrap">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500 hidden md:inline-flex items-center gap-1.5">
            <Search size={13}/> Filter
          </span>
          <select
            value={filters.status}
            onChange={(e) => setF((f) => ({ ...f, status: e.target.value }))}
            className="text-sm border border-slate-300 rounded-lg px-3 py-1.5 bg-white focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400"
            data-testid="pennylane-filter-status"
          >
            <option value="For Sale">Available now</option>
            <option value="all">Available &amp; under offer</option>
            <option value="Under Offer">Under offer only</option>
          </select>
          <select
            value={filters.min_beds}
            onChange={(e) => setF((f) => ({ ...f, min_beds: e.target.value }))}
            className="text-sm border border-slate-300 rounded-lg px-3 py-1.5 bg-white focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400"
            data-testid="pennylane-filter-beds"
          >
            <option value="">Any bedrooms</option>
            <option value="1">1+</option>
            <option value="2">2+</option>
            <option value="3">3+</option>
            <option value="4">4+</option>
          </select>
          {filters.status === 'For Sale' && counts.under_offer > 0 && (
            <button
              type="button"
              onClick={() => setF((f) => ({ ...f, status: 'Under Offer' }))}
              className="text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-50 text-amber-800 ring-1 ring-amber-200 hover:bg-amber-100 transition-colors"
              data-testid="under-offer-badge"
            >
              {counts.under_offer} more under offer
            </button>
          )}
          <div className="ml-auto text-xs text-slate-500">
            Showing <b className="text-slate-900">{data.items?.length || 0}</b> of {data.total || 0}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-6xl mx-auto px-6 py-8">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({length: 6}).map((_, i) => (
              <div key={i} className="aspect-[4/3] bg-slate-200 rounded-2xl animate-pulse"/>
            ))}
          </div>
        ) : data.items?.length === 0 ? (
          <p className="text-center py-16 text-slate-500">No properties match those filters.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="pennylane-grid">
            {data.items.map((p) => <PropertyCard key={p.id} p={p} onOpen={setOpenId}/>)}
          </div>
        )}
      </section>

      {/* Attribution */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-6 text-center">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-cyan-700 mb-2">How this page works</p>
          <h3 className="text-2xl font-bold text-slate-900">Every agent, one running order</h3>
          <p className="mt-2 text-sm text-slate-600 max-w-xl mx-auto">
            Listings come from the agents&apos; own feeds and from OnTheMarket, and the order is
            reshuffled between firms each day so no branch owns the top of the page.
            Viewings, offers and prices are handled by the selling agent named on each home.
          </p>
        </div>
      </section>

      <PropertyDetail id={openId} onClose={() => setOpenId(null)}/>
      <Footer />
    </div>
  );
};

export default PennyLaneProperties;
